import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Loader,
  Link2,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  TriangleAlert,
  Axe,
  Layers,
  LucideIcon,
  User,
  AlertCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { useStaffListSearch } from '../../hooks/useStaffListSearch';
import { kanbanApiService } from '../../services/kanbanApi';
import { elasticsearchService } from '../../services/elasticsearchService';
import { notesApiService } from '../../services/notesApi';
import { incidentsApiService } from '../../services/incidentsApi';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { modpackNotesApiService } from '../../services/modpackNotesApi';
import { calendarApiService } from '../../services/calendarApi';
import { appealsApiService } from '../../services/appealsApi';
import { usernameCacheService, useUsernameCacheVersion } from '../../services/usernameCache';
import { PlayerProfile } from '../PlayerProfile';
import type { AssociatedItem } from '../../types/player';
import { KANBAN_LINK_TYPES, LINK_TYPE_CONFIG, type KanbanCardLinkType } from '../../types/kanban';
import { cn } from '../../utils/cn';

interface PlayerEsHit {
  _id: string;
  _source: { uuid: string; lastKnownName: string; nickname: string; lastKnownIp: string };
}

interface AddCardLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardId: string;
  boardId?: string | null;
  onCreated: () => void;
}

const NOTES_FLOW_TYPES = new Set<KanbanCardLinkType>(['note', 'warning', 'ban']);
const OTHER_LINK_TYPES = KANBAN_LINK_TYPES.filter((t) => !NOTES_FLOW_TYPES.has(t));

function isSearchable(t: KanbanCardLinkType): t is Exclude<KanbanCardLinkType, 'ticket'> {
  return t !== 'ticket';
}

type WorkflowTab = 'notes_flow' | 'other';

const WORKFLOW_TABS: { id: WorkflowTab; label: string; icon: LucideIcon }[] = [
  { id: 'notes_flow', label: 'Notes, warnings & bans', icon: FileText },
  { id: 'other', label: 'Other ARGUS links', icon: Layers },
];

export const AddCardLinkModal = ({
  isOpen,
  onClose,
  cardId,
  boardId,
  onCreated,
}: AddCardLinkModalProps) => {
  const modalRef = useRef<HTMLFormElement>(null);

  const [workflowTab, setWorkflowTab] = useState<WorkflowTab>('notes_flow');
  const [linkType, setLinkType] = useState<KanbanCardLinkType>('note');
  const [linkTargetId, setLinkTargetId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onClickOutside = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);
  useOnClickOutside(modalRef, onClickOutside);
  const [error, setError] = useState<string | null>(null);
  /** Notes tab: paste ID instead of picking from recent grid (matches Add Evidence link tab). */
  const [notesPasteOpen, setNotesPasteOpen] = useState(false);
  /** Other tab: show raw target ID field when search is not enough. */
  const [otherTargetManualOpen, setOtherTargetManualOpen] = useState(false);
  const [manualNotesKind, setManualNotesKind] = useState<'note' | 'warning' | 'ban'>('note');

  const [recentItems, setRecentItems] = useState<AssociatedItem[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilters, setTypeFilters] = useState<Array<'note' | 'warning' | 'ban'>>(['note', 'warning', 'ban']);

  const [genericSearchQuery, setGenericSearchQuery] = useState('');
  const debouncedSearch = useDebounce(genericSearchQuery, 300);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [playerHits, setPlayerHits] = useState<PlayerEsHit[]>([]);
  const [genericResults, setGenericResults] = useState<{ id: string; primary: string; secondary?: string }[]>([]);

  const debouncedStaffQuery = useDebounce(
    linkType === 'build_team_member' ? genericSearchQuery : '',
    300,
  );
  const {
    matches: staffMatches,
    searching: staffSearching,
    error: staffSearchError,
  } = useStaffListSearch({
    debouncedQuery: debouncedStaffQuery,
    enabled: isOpen && workflowTab === 'other' && linkType === 'build_team_member',
  });
  const usernameCacheVersion = useUsernameCacheVersion();
  const staffSearchRows = useMemo(
    () =>
      staffMatches.map((s) => ({
        uuid: s.minecraft_uuid,
        name: usernameCacheService.getName(s.minecraft_uuid) ?? s.minecraft_uuid,
      })),
    [staffMatches, usernameCacheVersion],
  );

  const ActiveIcon = WORKFLOW_TABS.find((t) => t.id === workflowTab)?.icon ?? Link2;

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSubmitting(false);
    setWorkflowTab('notes_flow');
    setLinkType('note');
    setLinkTargetId('');
    setNotes('');
    setGenericSearchQuery('');
    setSearchQuery('');
    setPlayerHits([]);
    setGenericResults([]);
    setSearchError(null);
    setNotesPasteOpen(false);
    setOtherTargetManualOpen(false);
    setManualNotesKind('note');
    setTypeFilters(['note', 'warning', 'ban']);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || workflowTab !== 'notes_flow') return;
    let cancelled = false;
    (async () => {
      setIsLoadingRecent(true);
      try {
        const items = await notesApiService.getRecentItems();
        if (!cancelled) setRecentItems(items);
      } catch {
        if (!cancelled) setError('Failed to load recent notes and warnings.');
      } finally {
        if (!cancelled) setIsLoadingRecent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, workflowTab]);

  useEffect(() => {
    if (!isOpen || workflowTab !== 'other') return;
    setNotesPasteOpen(false);
    setGenericSearchQuery('');
    setPlayerHits([]);
    setGenericResults([]);
    setSearchError(null);
    setLinkTargetId('');
    setOtherTargetManualOpen(false);
    setLinkType((prev) => (NOTES_FLOW_TYPES.has(prev) ? OTHER_LINK_TYPES[0] ?? 'incident' : prev));
  }, [workflowTab, isOpen]);

  useEffect(() => {
    if (!isOpen || workflowTab !== 'other') return;
    setGenericSearchQuery('');
    setPlayerHits([]);
    setGenericResults([]);
    setSearchError(null);
    setLinkTargetId('');
  }, [linkType, isOpen, workflowTab]);

  const handleToggleFilter = (type: 'note' | 'warning' | 'ban') => {
    setTypeFilters((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const selectNotesItem = (item: AssociatedItem) => {
    setLinkType(item.type);
    setLinkTargetId(item.id);
    setNotesPasteOpen(false);
    setSearchQuery('');
  };

  const runSearch = useCallback(async () => {
    if (!isOpen || workflowTab !== 'other') return;
    const q = debouncedSearch.trim();
    if (q.length < 2) {
      setPlayerHits([]);
      setGenericResults([]);
      return;
    }

    setSearchError(null);
    setPlayerHits([]);
    setGenericResults([]);

    const ql = q.toLowerCase();

    try {
      if (linkType === 'player') {
        const res = await elasticsearchService.searchPlayers(q);
        setPlayerHits(res.hits?.hits ?? []);
        return;
      }

      if (linkType === 'note' || linkType === 'warning') {
        const type = linkType === 'note' ? 'note' : 'warning';
        const [p1, p2] = await Promise.all([
          notesApiService.getAllEntries(1, 150, type),
          notesApiService.getAllEntries(2, 150, type),
        ]);
        const merged = [...(p1.results ?? []), ...(p2.results ?? [])];
        const rows = merged.filter(
          (item: { id?: string; content?: string; player_name?: string }) =>
            (item.id && item.id.toLowerCase().includes(ql)) ||
            (item.content && String(item.content).toLowerCase().includes(ql)) ||
            (item.player_name && String(item.player_name).toLowerCase().includes(ql)),
        );
        setGenericResults(
          rows.slice(0, 25).map((item: { id: string; content?: string; type?: string }) => ({
            id: item.id,
            primary: (item.content || '').slice(0, 120) || item.id,
            secondary: item.type,
          })),
        );
        return;
      }

      if (linkType === 'ban') {
        const [b1, b2] = await Promise.all([
          notesApiService.getAllBans(1, 150),
          notesApiService.getAllBans(2, 150),
        ]);
        const merged = [...(b1.results ?? []), ...(b2.results ?? [])];
        const rows = merged.filter(
          (ban: { ban_uuid?: string; reason?: string; player_name?: string }) =>
            (ban.ban_uuid && ban.ban_uuid.toLowerCase().includes(ql)) ||
            (ban.reason && String(ban.reason).toLowerCase().includes(ql)) ||
            (ban.player_name && String(ban.player_name).toLowerCase().includes(ql)),
        );
        setGenericResults(
          rows.slice(0, 25).map((ban: { ban_uuid: string; reason?: string; player_name?: string }) => ({
            id: ban.ban_uuid,
            primary: ban.player_name || ban.ban_uuid,
            secondary: ban.reason ? String(ban.reason).slice(0, 100) : undefined,
          })),
        );
        return;
      }

      if (linkType === 'incident') {
        const [i1, i2] = await Promise.all([
          incidentsApiService.getAllIncidents(1, 150),
          incidentsApiService.getAllIncidents(2, 150),
        ]);
        const merged = [...(i1.results ?? []), ...(i2.results ?? [])];
        const rows = merged.filter(
          (inc: { id: string; title?: string; description?: string }) =>
            inc.id.toLowerCase().includes(ql) ||
            (inc.title && inc.title.toLowerCase().includes(ql)) ||
            (inc.description && inc.description.toLowerCase().includes(ql)),
        );
        setGenericResults(
          rows.slice(0, 25).map((inc: { id: string; title?: string }) => ({
            id: inc.id,
            primary: inc.title || inc.id,
          })),
        );
        return;
      }

      if (linkType === 'calendar_event') {
        const now = Date.now();
        const day = 24 * 60 * 60 * 1000;
        const events = await calendarApiService.getAllEvents({
          start_time: now - 30 * day,
          end_time: now + 120 * day,
        });
        const rows = (events ?? []).filter(
          (ev: { id: string; title?: string }) =>
            ev.id.toLowerCase().includes(ql) || (ev.title && ev.title.toLowerCase().includes(ql)),
        );
        setGenericResults(
          rows.slice(0, 25).map((ev: { id: string; title?: string }) => ({
            id: ev.id,
            primary: ev.title || ev.id,
          })),
        );
        return;
      }

      if (linkType === 'partnership') {
        const rows = await partnershipsApiService.searchPartnerships(q, 1, 25);
        setGenericResults(
          rows.map((p) => ({
            id: p.id,
            primary: p.summary?.slice(0, 120) || `Partnership ${p.id.slice(0, 8)}…`,
            secondary: p.status,
          })),
        );
        return;
      }

      if (linkType === 'partnership_creator') {
        const rows = await partnershipsApiService.searchCreators(q, 1, 25);
        setGenericResults(rows.map((c) => ({ id: c.id, primary: c.name, secondary: c.type })));
        return;
      }

      if (linkType === 'partnership_modpack') {
        const rows = await partnershipsApiService.searchModpacks(q, 1, 25);
        setGenericResults(
          rows.map((m) => ({ id: m.id, primary: m.name, secondary: m.platform_slug || undefined })),
        );
        return;
      }

      if (linkType === 'build') {
        const rows = await buildTeamApiService.searchBuildItems(q, 1, 25);
        setGenericResults(
          rows.map((b) => ({
            id: b.id,
            primary: b.title || b.id,
            secondary: b.status,
          })),
        );
        return;
      }

      if (linkType === 'build_project') {
        const rows = await buildTeamApiService.searchProjects(q, 1, 25);
        setGenericResults(
          rows.map((p) => ({
            id: p.id,
            primary: p.title || p.id,
            secondary: p.status,
          })),
        );
        return;
      }

      if (linkType === 'modpack_note') {
        const res = await modpackNotesApiService.searchNotes(q, 1, 25);
        setGenericResults(
          (res.results ?? []).map((n) => ({
            id: n.id,
            primary: (n.content || '').slice(0, 120) || n.id,
            secondary: n.fqdn || undefined,
          })),
        );
        return;
      }

      if (linkType === 'evidence') {
        const rows = await notesApiService.searchEvidenceByFilename(q);
        setGenericResults(
          (rows ?? []).slice(0, 25).map((e) => ({
            id: e.id,
            primary: e.filename,
            secondary: e.item_type,
          })),
        );
        return;
      }

      if (linkType === 'kanban_card') {
        const { results } = await kanbanApiService.searchCards({
          q,
          board_id: boardId || undefined,
          limit: 25,
        });
        setGenericResults(
          (results ?? [])
            .filter((c) => c.id !== cardId)
            .map((c) => ({
              id: c.id,
              primary: c.title,
              secondary: c.card_type,
            })),
        );
        return;
      }

      if (linkType === 'appeal') {
        const raw = await appealsApiService.listAppeals({ limit: 200, page: 1 });
        const list = Array.isArray(raw) ? raw : raw.results;
        const digits = q.replace(/\D/g, '');
        const n = digits ? Number.parseInt(digits, 10) : NaN;
        const rows = (list ?? []).filter((a) => {
          if (Number.isFinite(n) && n > 0 && a.id === n) return true;
          return (
            String(a.id).includes(q) ||
            (a.username && a.username.toLowerCase().includes(ql)) ||
            (a.ban_reason && a.ban_reason.toLowerCase().includes(ql))
          );
        });
        setGenericResults(
          rows.slice(0, 25).map((a) => ({
            id: String(a.id),
            primary: `#${a.id} · ${a.username}`,
            secondary: a.status,
          })),
        );
        return;
      }
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    }
  }, [isOpen, workflowTab, debouncedSearch, linkType, boardId, cardId]);

  useEffect(() => {
    if (!isOpen || workflowTab !== 'other' || !isSearchable(linkType)) return;
    if (linkType === 'build_team_member') return;

    if (debouncedSearch.trim().length < 2) {
      setPlayerHits([]);
      setGenericResults([]);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    void (async () => {
      try {
        await runSearch();
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, workflowTab, linkType, debouncedSearch, runSearch]);

  const resolveBuildMemberId = async (minecraftUuid: string) => {
    setSearchLoading(true);
    setSearchError(null);
    try {
      const member = await buildTeamApiService.getTeamMemberByUuid(minecraftUuid);
      setLinkTargetId(member.id);
      setGenericSearchQuery(member.display_name || minecraftUuid);
    } catch {
      setSearchError('That player is not registered as a build team member.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const target = linkTargetId.trim();
    if (!target) {
      setError('Select an item from the list or enter a target ID.');
      return;
    }

    const effectiveLinkType: KanbanCardLinkType =
      workflowTab === 'notes_flow' ? (notesPasteOpen ? manualNotesKind : linkType) : linkType;

    setIsSubmitting(true);
    setError(null);

    try {
      await kanbanApiService.createCardLink(cardId, {
        link_type: effectiveLinkType,
        link_target_id: target,
        notes: notes.trim() || undefined,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add link.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const searchable = workflowTab === 'other' && isSearchable(linkType);
  const isTicketLink = workflowTab === 'other' && linkType === 'ticket';
  const showStaffPicker = workflowTab === 'other' && linkType === 'build_team_member';
  const showPlayerEsPicker = workflowTab === 'other' && linkType === 'player';

  const filteredRecent = recentItems.filter((item) => {
    const sq = searchQuery.toLowerCase();
    const searchMatch =
      (item.content || '').toLowerCase().includes(sq) ||
      (item.creator || '').toLowerCase().includes(sq) ||
      (item.user_uuid || '').toLowerCase().includes(sq);
    const typeMatch = typeFilters.includes(item.type);
    return searchMatch && typeMatch;
  });

  const modalContent = (
    <div className="modal-overlay z-[99999]" style={{ zIndex: 99999 }}>
      <form
        ref={modalRef}
        onSubmit={handleSubmit}
        className="modal-panel min-h-[360px] z-[99999] max-w-md"
        style={{ zIndex: 99999 }}
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close z-10"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-4 pt-1 px-6">
          <span className="icon-tile icon-tile-accent icon-tile-lg mx-auto mb-3">
            <ActiveIcon className="w-5 h-5" />
          </span>
          <h2 className="modal-title">Link card</h2>
          <p className="modal-subtitle">Same layout as notes and warnings: pick from recent items or search, then submit.</p>
        </div>

        <div className="mb-4">
          <nav className="tabs" aria-label="Link workflow tabs" role="tablist">
            {WORKFLOW_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setWorkflowTab(tab.id)}
                  disabled={isSubmitting}
                  className="tab touch-manipulation shrink-0 disabled:opacity-50"
                  data-active={tab.id === workflowTab ? 'true' : 'false'}
                  role="tab"
                  aria-selected={tab.id === workflowTab}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 pb-1">
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {workflowTab === 'notes_flow' && (
            <div className="flex flex-col h-full">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="form-input w-full pl-9"
                  placeholder="Search by content, creator, or user UUID..."
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs font-medium text-glass-400">Filter:</span>
                <button
                  type="button"
                  onClick={() => handleToggleFilter('note')}
                  className={cn(
                    'badge touch-manipulation cursor-pointer',
                    typeFilters.includes('note') ? 'badge-info' : 'badge-neutral opacity-70',
                  )}
                >
                  <FileText className="w-3 h-3" />
                  <span>Notes</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleFilter('warning')}
                  className={cn(
                    'badge touch-manipulation cursor-pointer',
                    typeFilters.includes('warning') ? 'badge-warning' : 'badge-neutral opacity-70',
                  )}
                >
                  <TriangleAlert className="w-3 h-3" />
                  <span>Warnings</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleFilter('ban')}
                  className={cn(
                    'badge touch-manipulation cursor-pointer',
                    typeFilters.includes('ban') ? 'badge-danger' : 'badge-neutral opacity-70',
                  )}
                >
                  <Axe className="w-3 h-3" />
                  <span>Bans</span>
                </button>
              </div>

              {isLoadingRecent ? (
                <div className="flex flex-grow items-center justify-center min-h-[200px]">
                  <Loader className="w-6 h-6 animate-spin text-blue-400" />
                </div>
              ) : (
                <div className="flex-grow overflow-y-auto -mr-2 pr-2 space-y-2 max-h-[220px] grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {filteredRecent.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => selectNotesItem(item)}
                      disabled={isSubmitting}
                      className={cn(
                        'subcard w-full text-left p-3 transition-colors touch-manipulation',
                        linkTargetId === item.id && linkType === item.type
                          ? 'border-accent-400/70 ring-1 ring-accent-400/30 bg-accent-500/10'
                          : 'hover:border-glass-200/25',
                      )}
                    >
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className="flex items-center space-x-2 min-w-0">
                          <span
                            className={cn(
                              'badge shrink-0',
                              item.type === 'ban' && 'bg-red-500/15 text-red-300',
                              item.type === 'warning' && 'bg-yellow-500/15 text-yellow-300',
                              item.type === 'note' && 'bg-blue-500/15 text-blue-300',
                            )}
                          >
                            {item.type}
                          </span>
                          <PlayerProfile uuid={item.user_uuid} inline showUuid={false} />
                        </div>
                        <span className="text-xs text-glass-400 shrink-0 tabular-nums">
                          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-white truncate mb-2">{item.content}</p>
                      <div className="text-xs text-glass-400 flex items-center gap-1">
                        <User className="w-3 h-3 shrink-0" />
                        <span className="truncate">by {item.creator}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setNotesPasteOpen((v) => !v);
                  if (!notesPasteOpen) setLinkTargetId('');
                }}
                className="btn-ghost btn-xs mt-3 -ml-2"
              >
                {notesPasteOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                Paste item ID manually (same as notes link tab)
              </button>

              {notesPasteOpen && (
                <div className="subcard mt-3 space-y-2 p-3">
                  <label htmlFor="manual-notes-kind" className="text-xs font-medium text-glass-400 mb-1.5 block">
                    Item type
                  </label>
                  <select
                    id="manual-notes-kind"
                    value={manualNotesKind}
                    onChange={(e) => setManualNotesKind(e.target.value as 'note' | 'warning' | 'ban')}
                    className="form-input w-full text-sm"
                    disabled={isSubmitting}
                  >
                    <option value="note">Note</option>
                    <option value="warning">Warning</option>
                    <option value="ban">Ban</option>
                  </select>
                  <label htmlFor="manual-item-id" className="text-xs font-medium text-glass-400 mb-1.5 block">
                    Item ID
                  </label>
                  <input
                    id="manual-item-id"
                    type="text"
                    value={linkTargetId}
                    onChange={(e) => setLinkTargetId(e.target.value)}
                    className="form-input w-full font-mono text-sm"
                    placeholder="Paste an Item ID directly..."
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          )}

          {workflowTab === 'other' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="card-link-type" className="text-xs font-medium text-glass-400 mb-1.5 block">
                  Link type
                </label>
                <select
                  id="card-link-type"
                  value={linkType}
                  onChange={(e) => setLinkType(e.target.value as KanbanCardLinkType)}
                  className="form-input w-full text-sm"
                  disabled={isSubmitting}
                >
                  {OTHER_LINK_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {LINK_TYPE_CONFIG[t].label}
                    </option>
                  ))}
                </select>
              </div>

              {isTicketLink && (
                <p className="alert alert-warning text-xs !py-2">
                  Ticket links use the numeric ticket ID. Enter it under Target ID below (search is not available for tickets yet).
                </p>
              )}

              {searchable && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-glass-400 mb-1.5 block">Find target</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                    <input
                      type="search"
                      value={genericSearchQuery}
                      onChange={(e) => setGenericSearchQuery(e.target.value)}
                      placeholder={
                        linkType === 'appeal'
                          ? 'Appeal #, username, or ban reason...'
                          : linkType === 'player'
                            ? 'Player name or UUID...'
                            : 'Type at least 2 characters...'
                      }
                      className="form-input w-full pl-9 text-sm"
                      autoComplete="off"
                      disabled={isSubmitting}
                    />
                    {(searchLoading || staffSearching) && (
                      <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
                    )}
                  </div>
                  {genericSearchQuery.length > 0 && genericSearchQuery.length < 2 && (
                    <p className="text-xs text-glass-500">Type at least 2 characters...</p>
                  )}
                  {(searchError || staffSearchError) && (
                    <p className="text-xs text-red-400">{searchError || staffSearchError}</p>
                  )}

                  {showStaffPicker &&
                    debouncedStaffQuery.length >= 2 &&
                    !staffSearching &&
                    !staffSearchError &&
                    staffSearchRows.length === 0 && (
                      <p className="text-xs text-glass-500">No staff match. Only registered build team members can be linked.</p>
                    )}

                  {showStaffPicker && staffSearchRows.length > 0 && (
                    <div className="subcard overflow-hidden max-h-[220px] overflow-y-auto">
                      {staffSearchRows.map((row) => (
                        <button
                          key={row.uuid}
                          type="button"
                          disabled={isSubmitting || searchLoading}
                          onClick={() => void resolveBuildMemberId(row.uuid)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-glass-200/10 transition-colors text-sm disabled:opacity-50"
                        >
                          <PlayerProfile uuid={row.uuid} name={row.name} inline showUuid={false} />
                          <span className="text-[10px] text-glass-500 font-mono shrink-0">{row.uuid.slice(0, 8)}...</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {showPlayerEsPicker && debouncedSearch.length >= 2 && !searchLoading && !searchError && playerHits.length === 0 && (
                    <p className="text-xs text-glass-500">No players found.</p>
                  )}

                  {showPlayerEsPicker && playerHits.length > 0 && (
                    <div className="subcard overflow-hidden max-h-[220px] overflow-y-auto">
                      {playerHits.map((hit) => (
                        <button
                          key={hit._id}
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => {
                            setLinkTargetId(hit._source.uuid);
                            setGenericSearchQuery(hit._source.lastKnownName || hit._source.uuid);
                          }}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-glass-200/10 transition-colors text-sm"
                        >
                          <PlayerProfile
                            uuid={hit._source.uuid}
                            name={hit._source.lastKnownName || hit._source.nickname}
                            inline
                            showUuid={false}
                          />
                          <span className="text-[10px] text-glass-500 font-mono shrink-0">{hit._source.uuid.slice(0, 8)}...</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {!showStaffPicker && !showPlayerEsPicker && genericResults.length > 0 && (
                    <div className="subcard overflow-hidden max-h-[220px] overflow-y-auto">
                      {genericResults.map((row) => (
                        <button
                          key={row.id}
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => {
                            setLinkTargetId(row.id);
                            setGenericSearchQuery(row.primary);
                          }}
                          className="w-full text-left px-3 py-2.5 hover:bg-glass-200/10 transition-colors border-b border-glass-200/10 last:border-0"
                        >
                          <p className="text-sm text-white truncate">{row.primary}</p>
                          {row.secondary && <p className="text-xs text-glass-500 truncate mt-0.5">{row.secondary}</p>}
                          <p className="text-[10px] font-mono text-glass-600 mt-1">{row.id}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {!showStaffPicker &&
                    !showPlayerEsPicker &&
                    debouncedSearch.length >= 2 &&
                    !searchLoading &&
                    !searchError &&
                    genericResults.length === 0 && <p className="text-xs text-glass-500">No matches.</p>}

                  {linkTargetId && (
                    <div className="alert alert-info text-xs !py-2 items-center justify-between">
                      <span>Selected</span>
                      <span className="font-mono truncate max-w-[200px]" title={linkTargetId}>
                        {linkTargetId}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {workflowTab === 'other' && (!searchable || otherTargetManualOpen || isTicketLink) && (
                <div>
                  <label htmlFor="card-link-target" className="text-xs font-medium text-glass-400 mb-1.5 block">
                    Target ID
                  </label>
                  <input
                    id="card-link-target"
                    type="text"
                    value={linkTargetId}
                    onChange={(e) => setLinkTargetId(e.target.value)}
                    className="form-input w-full text-sm font-mono"
                    placeholder="Entity ID"
                    disabled={isSubmitting}
                    required={isTicketLink}
                  />
                </div>
              )}

              {workflowTab === 'other' && searchable && !isTicketLink && (
                <button
                  type="button"
                  onClick={() => setOtherTargetManualOpen((v) => !v)}
                  className="btn-ghost btn-xs -ml-2"
                >
                  {otherTargetManualOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  Enter target ID manually
                </button>
              )}
            </div>
          )}

          <div className="mt-4">
            <label htmlFor="card-link-notes" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Notes (optional)
            </label>
            <textarea
              id="card-link-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="form-input w-full min-h-[72px] resize-y"
              rows={3}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="modal-footer flex-shrink-0 mt-auto">
          <div className="flex flex-col sm:flex-row justify-end gap-2 w-full">
            <button type="button" onClick={onClose} className="glass-button-secondary w-full sm:w-auto touch-manipulation" disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary w-full sm:w-auto touch-manipulation"
              disabled={isSubmitting || !linkTargetId.trim()}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Add link
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  return createPortal(modalContent, document.body);
};
